import Button from "@src/straps/base/buttons/Button/Button";
import CopyableContent from "@src/straps/derived/CopyableContent/CopyableContent";
import { ComponentProps, FunctionComponent } from "react";

const backpackEmailAliases = ["support", "professionals", "success"] as const;

const CopyableLink: FunctionComponent<
  React.PropsWithChildren<
    Readonly<
      Pick<ComponentProps<typeof Button>, "noFrameVariant" | "color" | "href"> &
        Pick<
          ComponentProps<typeof CopyableContent>,
          | "iconBackgroundColor"
          | "onHoveredMessage"
          | "onCopiedMessage"
          | "value"
          | "alignTooltip"
        >
    >
  >
> = ({
  noFrameVariant = "hyperlink",
  color,
  href,
  children,
  ...copyableContentProps
}) => (
  <CopyableContent {...copyableContentProps}>
    <Button
      component="a"
      href={href}
      noFrameVariant={noFrameVariant}
      dataTestId="copyable-link-button"
      color={color}
    >
      {children}
    </Button>
  </CopyableContent>
);

/**
 * The `email` prop default value is `"support"` - this means that `<ContactUsLink />` will render a link to `mailto:support@backpacknetworks.com`
 * and a visible and copyable value of `support@backpacknetworks.com` unless `"professionals" | "success"` is passed.
 */
export default function ContactUsLink({
  email = "support",
  ...props
}: Readonly<
  Pick<
    ComponentProps<typeof CopyableLink>,
    "color" | "noFrameVariant" | "alignTooltip" | "iconBackgroundColor"
  > & { email?: (typeof backpackEmailAliases)[number] }
>) {
  const emailAddress = `${email}@backpacknetworks.com`;
  return (
    <CopyableLink
      value={emailAddress}
      href={`mailto:${emailAddress}`}
      onHoveredMessage="Copy email"
      onCopiedMessage="Email copied!"
      {...props}
    >
      {emailAddress}
    </CopyableLink>
  );
}
